import { useApolloClient } from "@apollo/client";
import { assertNever } from "@msys/common";
import {
  DataGrid,
  DebouncedSearchInput,
  GridColDef,
  LabeledValue,
  ListHeader,
  ModalActions,
  ModalContent,
  ModalOpenButton,
  ModalTitle,
  ModalWithSidePanelDialog,
  ModalWithSidePanelSections,
} from "@msys/ui";
import {
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardDoubleArrowLeft as KeyboardDoubleArrowLeftIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, useFormikContext } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { ButtonCreate } from "../../../commons/button/Button.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { SelectedBoxWithIndicator } from "../../../commons/SelectedBoxWithIndicator.js";
import { UserAvatar } from "../../users/UserAvatar.js";
import { useOrganisationRoles } from "../../users/useRoles.js";
import {
  OrganisationAutomatedWorkflowsBox_OrganisationWorkflowFragment,
  useCreateOrganisationWorkflowMutation,
  useModifyOrganisationWorkflowSettingsMutation,
} from "../boxes/OrganisationAutomatedWorkflowsBox.generated.js";
import { useAddOrganisationEmailTemplateMutation } from "../boxes/OrganisationEmailSettingsBox.generated.js";
import { useEmailTemplateContexts } from "../boxes/useEmailTemplateContexts.js";
import {
  useOrganisationMemberSelect_MemberOptionsQuery,
  useOrganisationMemberSelect_SelectedMembersQuery,
} from "../OrganisationMemberSelect.generated.js";
import { OrganisationEmailTemplateModal } from "./OrganisationEmailTemplateModal.js";

const SOURCE_TYPES = [
  "Manual",
  "FlowWizard",
  "Flow3D",
  "FlowVoiceAssistant",
  "FlowM1",
  "RequestManual",
  "RequestAutomated",
  "Import",
  "QuoteProductsDistribution",
  "ContracteeInvitation",
];

const WORKFLOW_LABELS: Record<string, { title: string; description: string }> =
  {
    customerConfirmationWorkflow: {
      title: "Send quote confirmation to customer",
      description:
        "Sends the customer a confirmation email when a quote is created",
    },
    contractorNotificationWorkflow: {
      title: "Notify contractor about new lead",
      description:
        "Sends an internal notification email to the contractor when a new lead is received",
    },
    customerQuotePdfWorkflow: {
      title: "Send quote details with PDF to customer",
      description:
        "Sends the customer an email with the standard Quote PDF attached when a quote is created",
    },
  };

const WORKFLOW_TYPES = Object.keys(WORKFLOW_LABELS);

const WORKFLOW_DEFAULTS: Record<
  string,
  {
    pdfTitle?: string;
  }
> = {
  customerQuotePdfWorkflow: {
    pdfTitle: "",
  },
};

type SidePanelState =
  | { status: "closed" }
  | { status: "email-template-select" }
  | { status: "email-template-create" }
  | { status: "source-type-select" }
  | { status: "recipient-select" };

interface FormValues {
  workflowType: string;
  description: string;
  emailTemplateId: string | null;
  sourceTypeFilter: string[];
  recipientUserId: string | null;
  pdfTitle: string;
}

interface Props {
  workflow?: OrganisationAutomatedWorkflowsBox_OrganisationWorkflowFragment;
  emailTemplates: {
    id: string;
    description: string;
    context: string;
    attachments: { id: string }[];
  }[];
  organisationId?: string;
  handleClose: () => void;
}

export const OrganisationWorkflowPanel = ({
  workflow,
  emailTemplates,
  organisationId,
  handleClose,
}: Props) => {
  const isEditMode = !!workflow;
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const formId = React.useMemo(() => uniqueId(), []);

  const [sidePanelState, setSidePanelState] = React.useState<SidePanelState>({
    status: "closed",
  });
  const showSideContent = sidePanelState.status !== "closed";

  const handleCloseSidePanel = React.useCallback(() => {
    setSidePanelState({ status: "closed" });
  }, []);

  // Mutations
  const [createWorkflow] = useCreateOrganisationWorkflowMutation({
    client,
    refetchQueries: [
      namedOperations.Query.OrganisationWorkflows,
      namedOperations.Query.OrganisationSettingsIntegrations,
    ],
  });

  const [modifyWorkflowSettings] =
    useModifyOrganisationWorkflowSettingsMutation({ client });

  const [addEmailTemplate] = useAddOrganisationEmailTemplateMutation({
    client,
    refetchQueries: [namedOperations.Query.OrganisationSettingsIntegrations],
  });

  // Options
  const workflowTypeOptions = React.useMemo(
    () =>
      WORKFLOW_TYPES.map(type => ({
        value: type,
        label: t(WORKFLOW_LABELS[type]!.title as any, {
          ns: "OrganisationSettings",
        }),
      })),
    [t]
  );

  const initialValues: FormValues = React.useMemo(
    () =>
      isEditMode
        ? {
            workflowType: workflow.workflowType,
            description: workflow.description,
            emailTemplateId: workflow.emailTemplateId ?? null,
            sourceTypeFilter: workflow.sourceTypeFilter ?? [],
            recipientUserId: workflow.recipientUserId ?? null,
            pdfTitle: workflow.pdfTitle ?? "",
          }
        : {
            workflowType: "",
            description: "",
            emailTemplateId: null,
            sourceTypeFilter: [],
            recipientUserId: null,
            pdfTitle: "",
          },
    [isEditMode, workflow]
  );

  const handleSubmit = React.useCallback(
    async (values: FormValues) => {
      try {
        if (isEditMode) {
          await modifyWorkflowSettings({
            variables: {
              workflowId: workflow.id,
              input: {
                description: values.description,
                emailTemplateId: values.emailTemplateId,
                sourceTypeFilter:
                  values.sourceTypeFilter.length > 0
                    ? values.sourceTypeFilter
                    : null,
                recipientUserId: values.recipientUserId,
                pdfTitle: values.pdfTitle || null,
              },
            },
          });
        } else {
          await createWorkflow({
            variables: {
              input: {
                workflowType: values.workflowType,
                description: values.description,
                emailTemplateId: values.emailTemplateId,
                sourceTypeFilter:
                  values.sourceTypeFilter.length > 0
                    ? values.sourceTypeFilter
                    : null,
                recipientUserId: values.recipientUserId,
                pdfTitle: values.pdfTitle || null,
              },
            },
          });
        }
        handleClose();
      } catch (error: any) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    },
    [
      isEditMode,
      workflow,
      createWorkflow,
      modifyWorkflowSettings,
      handleClose,
      enqueueSnackbar,
    ]
  );

  return (
    <ModalWithSidePanelDialog
      fixedHeight={showSideContent}
      dialogProps={{
        maxWidth: showSideContent ? "lg" : "sm",
      }}
      handleClose={handleClose}
    >
      <Formik<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {formikProps => (
          <ModalWithSidePanelSections
            sideContent={
              sidePanelState.status === "email-template-select" ||
              sidePanelState.status === "email-template-create" ? (
                <EmailTemplateSidePanel
                  emailTemplates={emailTemplates}
                  handleClose={handleCloseSidePanel}
                  addEmailTemplate={addEmailTemplate}
                />
              ) : sidePanelState.status === "source-type-select" ? (
                <SourceTypeSelectPanel handleClose={handleCloseSidePanel} />
              ) : sidePanelState.status === "recipient-select" ? (
                <RecipientSelectSidePanel handleClose={handleCloseSidePanel} />
              ) : sidePanelState.status === "closed" ? null : (
                assertNever(sidePanelState)
              )
            }
            maxWidth="sm"
            maxWidthWithSideContent="lg"
          >
            <ModalTitle
              title={
                isEditMode
                  ? t("Edit workflow" as any, { ns: "OrganisationSettings" })
                  : t("Create workflow" as any, { ns: "OrganisationSettings" })
              }
              handleClose={handleClose}
            />
            <ModalContent>
              <Form id={formId}>
                <Stack spacing={2}>
                  {!isEditMode && <WorkflowTypeWatcher />}

                  {/* Workflow type */}
                  {isEditMode ? (
                    <LabeledValue
                      label={t("Workflow type" as any, {
                        ns: "OrganisationSettings",
                      })}
                    >
                      {t(
                        (WORKFLOW_LABELS[workflow.workflowType]?.title ??
                          workflow.workflowType) as any,
                        { ns: "OrganisationSettings" }
                      )}
                    </LabeledValue>
                  ) : (
                    <>
                      <SelectField
                        name="workflowType"
                        label={t("Workflow type" as any, {
                          ns: "OrganisationSettings",
                        })}
                        options={workflowTypeOptions}
                      />
                      {formikProps.values.workflowType &&
                        WORKFLOW_LABELS[formikProps.values.workflowType] && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: -1 }}
                          >
                            {t(
                              WORKFLOW_LABELS[formikProps.values.workflowType]!
                                .description as any,
                              { ns: "OrganisationSettings" }
                            )}
                          </Typography>
                        )}
                    </>
                  )}

                  {/* Show remaining fields only after type selection */}
                  {(isEditMode || formikProps.values.workflowType) && (
                    <>
                      {/* Description */}
                      <TextField
                        name="description"
                        label={t("Description", { ns: "OrganisationSettings" })}
                        required={!isEditMode}
                      />

                      {/* Email template */}
                      <Box width="100%">
                        {formikProps.values.emailTemplateId ? (
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <LabeledValue
                              label={t("Email template" as any, {
                                ns: "OrganisationSettings",
                              })}
                            >
                              {emailTemplates.find(
                                et =>
                                  et.id === formikProps.values.emailTemplateId
                              )?.description ??
                                formikProps.values.emailTemplateId}
                            </LabeledValue>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => {
                                formikProps.setFieldValue(
                                  "emailTemplateId",
                                  null
                                );
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        ) : (
                          <SelectedBoxWithIndicator
                            selected={
                              sidePanelState.status ===
                                "email-template-select" ||
                              sidePanelState.status === "email-template-create"
                            }
                          >
                            <Button
                              size="small"
                              color="secondary"
                              variant="text"
                              startIcon={<AddIcon />}
                              sx={{ textWrap: "nowrap" }}
                              onClick={() =>
                                setSidePanelState({
                                  status: "email-template-select",
                                })
                              }
                            >
                              {t("Select email template" as any, {
                                ns: "OrganisationSettings",
                              })}
                            </Button>
                          </SelectedBoxWithIndicator>
                        )}
                      </Box>

                      {/* Source type filter */}
                      <Stack spacing={0.5}>
                        <Box width="100%">
                          {formikProps.values.sourceTypeFilter.length > 0 ? (
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <LabeledValue
                                label={t("Source type filter" as any, {
                                  ns: "OrganisationSettings",
                                })}
                              >
                                {formikProps.values.sourceTypeFilter.join(", ")}
                              </LabeledValue>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() =>
                                  setSidePanelState({
                                    status: "source-type-select",
                                  })
                                }
                              >
                                <EditIcon />
                              </IconButton>
                            </Stack>
                          ) : (
                            <SelectedBoxWithIndicator
                              selected={
                                sidePanelState.status === "source-type-select"
                              }
                            >
                              <Button
                                size="small"
                                color="secondary"
                                variant="text"
                                startIcon={<AddIcon />}
                                sx={{ textWrap: "nowrap" }}
                                onClick={() =>
                                  setSidePanelState({
                                    status: "source-type-select",
                                  })
                                }
                              >
                                {t("Select source types" as any, {
                                  ns: "OrganisationSettings",
                                })}
                              </Button>
                            </SelectedBoxWithIndicator>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ pl: 0.5 }}
                        >
                          {t(
                            "Filter which quote sources trigger this workflow. Leave empty for all sources." as any,
                            { ns: "OrganisationSettings" }
                          )}
                        </Typography>
                      </Stack>

                      {/* Recipient (contractorNotification only) */}
                      {formikProps.values.workflowType ===
                        "contractorNotificationWorkflow" && (
                        <Box width="100%">
                          {formikProps.values.recipientUserId ? (
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <LabeledValue
                                label={t("Recipient" as any, {
                                  ns: "OrganisationSettings",
                                })}
                              >
                                <RecipientButtonLabel />
                              </LabeledValue>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => {
                                  formikProps.setFieldValue(
                                    "recipientUserId",
                                    null
                                  );
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Stack>
                          ) : (
                            <SelectedBoxWithIndicator
                              selected={
                                sidePanelState.status === "recipient-select"
                              }
                            >
                              <Button
                                size="small"
                                color="secondary"
                                variant="text"
                                startIcon={<AddIcon />}
                                sx={{ textWrap: "nowrap" }}
                                onClick={() =>
                                  setSidePanelState({
                                    status: "recipient-select",
                                  })
                                }
                              >
                                {t("Select recipient" as any, {
                                  ns: "OrganisationSettings",
                                })}
                              </Button>
                            </SelectedBoxWithIndicator>
                          )}
                        </Box>
                      )}

                      {/* PDF title (customerQuotePdf only) */}
                      {formikProps.values.workflowType ===
                        "customerQuotePdfWorkflow" && (
                        <TextField
                          name="pdfTitle"
                          label={t("PDF title" as any, {
                            ns: "OrganisationSettings",
                          })}
                        />
                      )}
                    </>
                  )}
                </Stack>
              </Form>
            </ModalContent>
            <ModalActions
              actionButtons={[
                {
                  label: t("Cancel", { ns: "Global" }),
                  handleClick: handleClose,
                  buttonProps: {
                    variant: "text",
                    disabled: formikProps.isSubmitting,
                  },
                },
                {
                  label: isEditMode
                    ? t("Save", { ns: "Global" })
                    : t("Create", { ns: "Global" }),
                  buttonProps: {
                    type: "submit",
                    form: formId,
                    disabled:
                      !formikProps.values.emailTemplateId ||
                      (!isEditMode &&
                        (!formikProps.values.workflowType ||
                          !formikProps.values.description)),
                    loading: formikProps.isSubmitting,
                  },
                },
              ]}
            />
          </ModalWithSidePanelSections>
        )}
      </Formik>
    </ModalWithSidePanelDialog>
  );
};

// --- Helper Components ---

function RecipientButtonLabel() {
  const { t } = useTranslate(["OrganisationSettings"]);
  const { values } = useFormikContext<FormValues>();
  const client = useApolloClient();

  const selectedQuery = useOrganisationMemberSelect_SelectedMembersQuery({
    client,
    variables: {
      sorting: [{ column: "firstname", direction: "asc" }],
      selectedMembers: values.recipientUserId ? [values.recipientUserId] : [],
      limit: 1,
    },
    skip: !values.recipientUserId,
  });

  const selectedMember = selectedQuery.data?.selectedMembers.edges
    .map(e => e.node)
    .find(m => m.id === values.recipientUserId);

  if (selectedMember) {
    return <>{selectedMember.fullname}</>;
  }

  return (
    <>
      {t("Select recipient" as any, {
        ns: "OrganisationSettings",
      })}
    </>
  );
}

function WorkflowTypeWatcher() {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const prevRef = React.useRef(values.workflowType);

  React.useEffect(() => {
    if (values.workflowType && values.workflowType !== prevRef.current) {
      prevRef.current = values.workflowType;
      const defaults = WORKFLOW_DEFAULTS[values.workflowType] ?? {};
      setFieldValue("sourceTypeFilter", []);
      setFieldValue("pdfTitle", defaults.pdfTitle ?? "");
      setFieldValue("emailTemplateId", null);
      setFieldValue("recipientUserId", null);
    }
  }, [values.workflowType, setFieldValue]);

  return null;
}

// --- Side Panels ---

function EmailTemplateSidePanel({
  emailTemplates,
  handleClose,
  addEmailTemplate,
}: {
  emailTemplates: {
    id: string;
    description: string;
    context: string;
    attachments: { id: string }[];
  }[];
  handleClose: () => void;
  addEmailTemplate: ReturnType<
    typeof useAddOrganisationEmailTemplateMutation
  >[0];
}) {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { setFieldValue } = useFormikContext<FormValues>();
  const { labels: emailTemplateContextLabels } = useEmailTemplateContexts();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const filteredTemplates = React.useMemo(() => {
    if (!searchTerm) return emailTemplates;
    const lower = searchTerm.toLowerCase();
    return emailTemplates.filter(
      tpl =>
        tpl.description.toLowerCase().includes(lower) ||
        (
          emailTemplateContextLabels[
            tpl.context as keyof typeof emailTemplateContextLabels
          ] ?? ""
        )
          .toLowerCase()
          .includes(lower)
    );
  }, [emailTemplates, searchTerm, emailTemplateContextLabels]);

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "description",
        headerName: t("Template", { ns: "OrganisationSettings" }),
        flex: 1,
        sortable: false,
      },
      {
        field: "context",
        headerName: t("Context", { ns: "OrganisationSettings" }),
        sortable: false,
        valueGetter: (value: string) =>
          emailTemplateContextLabels[
            value as keyof typeof emailTemplateContextLabels
          ] ?? value,
      },
      {
        field: "attachmentCount",
        flex: 0,
        maxWidth: 50,
        align: "right" as const,
        sortable: false,
        renderHeader: () => <AttachFileIcon />,
        headerAlign: "right" as const,
        valueGetter: (_value: unknown, row: (typeof emailTemplates)[0]) =>
          row.attachments.length,
      },
    ],
    [t, emailTemplateContextLabels]
  );

  const headerActions = (
    <ModalOpenButton
      Modal={OrganisationEmailTemplateModal}
      modalProps={{
        title: t("New email template", { ns: "OrganisationSettings" }),
        handleComplete: async (values: any, modalHandleClose: () => void) => {
          await addEmailTemplate({ variables: { input: values } });
          modalHandleClose();
        },
      }}
    >
      <ButtonCreate
        variant="contained"
        color="primary"
        title={t("Create new" as any, { ns: "Global" })}
      />
    </ModalOpenButton>
  );

  return (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Select email template" as any, {
          ns: "OrganisationSettings",
        })}
        headerActions={headerActions}
      />
      <ModalContent>
        <Stack height="100%" spacing={1}>
          <ListHeader
            SearchField={
              <DebouncedSearchInput
                placeholder={t("Search", { ns: "Global" })}
                defaultValue={searchTerm}
                onChangeSearchTerm={newValue => setSearchTerm(newValue)}
              />
            }
            marginBottom={0}
          />
          <Box flex={1}>
            <DataGrid
              columns={columns}
              rows={filteredTemplates}
              rowSelectionModel={{
                type: "include",
                ids: new Set(selectedId ? [selectedId] : []),
              }}
              onRowClick={({ row }) => {
                setSelectedId(selectedId === row.id ? null : row.id);
              }}
              hideFooter
              paginationMode="client"
              disableColumnMenu
              disableColumnResize
              disableRowSelectionOnClick
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
            />
          </Box>
        </Stack>
      </ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
        <Button
          color="primary"
          variant="contained"
          disabled={!selectedId}
          onClick={() => {
            if (!selectedId) return;
            setFieldValue("emailTemplateId", selectedId);
            handleClose();
          }}
        >
          {t("Select", { ns: "Global" })}
        </Button>
      </DialogActions>
    </>
  );
}

function SourceTypeSelectPanel({ handleClose }: { handleClose: () => void }) {
  const { t } = useTranslate(["OrganisationSettings"]);
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const handleToggle = (sourceType: string) => {
    const current = values.sourceTypeFilter;
    if (current.includes(sourceType)) {
      setFieldValue(
        "sourceTypeFilter",
        current.filter(s => s !== sourceType)
      );
    } else {
      setFieldValue("sourceTypeFilter", [...current, sourceType]);
    }
  };

  return (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Source types" as any, { ns: "OrganisationSettings" })}
      />
      <ModalContent>
        <Stack spacing={0} overflow="auto">
          {SOURCE_TYPES.map(st => (
            <FormControlLabel
              key={st}
              control={
                <Checkbox
                  checked={values.sourceTypeFilter.includes(st)}
                  onChange={() => handleToggle(st)}
                  size="small"
                />
              }
              label={st}
            />
          ))}
        </Stack>
      </ModalContent>
      <DialogActions>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
      </DialogActions>
    </>
  );
}

function RecipientSelectSidePanel({
  handleClose,
}: {
  handleClose: () => void;
}) {
  const { t } = useTranslate([
    "OrganisationSettings",
    "OrganisationProfile",
    "Global",
  ]);

  const { setFieldValue } = useFormikContext<FormValues>();
  const client = useApolloClient();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const { getOrganisationRoleTitle } = useOrganisationRoles();

  const query = useOrganisationMemberSelect_MemberOptionsQuery({
    client,
    fetchPolicy: "network-only",
    variables: {
      sorting: [{ column: "firstname", direction: "asc" }],
      search: searchTerm || undefined,
    },
  });

  const members = React.useMemo(
    () =>
      (query.data ?? query.previousData)?.organisationMemberships.edges.map(
        edge => edge.node
      ) ?? [],
    [query.data, query.previousData]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "fullname",
        headerName: t("Name", { ns: "Global" }),
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <UserAvatar userAvatar={row} size="s" />
            <Typography>{row.fullname}</Typography>
          </Stack>
        ),
      },
      {
        field: "roles",
        headerName: t("Roles", { ns: "OrganisationProfile" }),
        flex: 1,
        sortable: false,
        valueGetter: (_value: unknown, row: any) =>
          getOrganisationRoleTitle(row.roles),
      },
      {
        field: "email",
        headerName: t("Email", { ns: "Global" }),
        flex: 1,
        sortable: false,
        valueGetter: (_value: unknown, row: any) => row.email,
      },
    ],
    [t, getOrganisationRoleTitle]
  );

  return (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Select recipient" as any, {
          ns: "OrganisationSettings",
        })}
      />
      <ModalContent>
        <Stack height="100%" spacing={1}>
          <ListHeader
            SearchField={
              <DebouncedSearchInput
                placeholder={t("Search", { ns: "Global" })}
                defaultValue={searchTerm}
                onChangeSearchTerm={newValue => setSearchTerm(newValue)}
              />
            }
            marginBottom={0}
          />
          <Box flex={1}>
            <DataGrid
              columns={columns}
              rows={members}
              loading={query.loading}
              rowSelectionModel={{
                type: "include",
                ids: new Set(selectedId ? [selectedId] : []),
              }}
              onRowClick={({ row }) => {
                setSelectedId(selectedId === row.id ? null : row.id);
              }}
              hideFooter
              paginationMode="client"
              disableColumnMenu
              disableColumnResize
              disableRowSelectionOnClick
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
            />
          </Box>
        </Stack>
      </ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
        <Button
          color="primary"
          variant="contained"
          disabled={!selectedId}
          onClick={() => {
            if (!selectedId) return;
            setFieldValue("recipientUserId", selectedId);
            handleClose();
          }}
        >
          {t("Select", { ns: "Global" })}
        </Button>
      </DialogActions>
    </>
  );
}
